/**
 * "The World" band (#3412 slice 2) — the state-2 general skim: Time (the IC
 * clock, plus the docked character's local weather — renamed from "The
 * Calendar", which read as a schedule and collided with Occasions; Dan's
 * ruling 2026-08-31), upcoming Occasions, and The Crier (gemits) are
 * account-independent ambiance; the persona tidings digest is docked-only
 * (tidings-split ruling — public awareness scopes to the ACTIVE character,
 * never the account, mirrors `TidingsPage`).
 *
 * Time renders the clock's `date_display` (#4185): the month names live on
 * the backend (`world/game_clock/constants.py`), never in the frontend.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plate, PlateHead } from '@/components/folio';
import { EventCard } from '@/events/components/EventCard';
import { fetchEvents } from '@/events/queries';
import type { EventListItem, PaginatedResponse } from '@/events/types';
import { useGemits } from '@/narrative/queries';
import { TidingsFeed } from '@/tidings/components/TidingsFeed';
import { useAppSelector } from '@/store/hooks';
import { useBrowsingIdentity } from '@/roster/useBrowsingIdentity';
import { useWeatherConditions } from '@/weather/queries';
import { useClockQuery } from './queries';

function capitalize(s: string): string {
  return s.length === 0 ? s : s[0].toUpperCase() + s.slice(1);
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function TimePlate() {
  const { data: clock } = useClockQuery();
  // Live-session room lookup stays keyed to THIS tab's own `active`/`sessions`
  // (ADR-0247), unrelated to browsing identity below.
  const { sessions, active } = useAppSelector((state) => state.game);
  const sessionRoomId = active ? (sessions[active]?.room?.id ?? null) : null;
  const { entryId: browsingEntryId } = useBrowsingIdentity();
  // With no live session room, the server resolves the docked character's own
  // room; the docked entry id rides along (#3479) so the read follows THIS
  // tab's browsing identity, not the account's durable selection. Weather
  // only reads for someone who IS somewhere, so the query stays off entirely
  // with nothing docked.
  const { data: conditions } = useWeatherConditions(sessionRoomId, {
    fallbackToSelection: browsingEntryId != null,
    entryId: browsingEntryId,
  });

  return (
    <Plate className="p-4">
      <PlateHead as="h2" className="mb-2">
        Time
      </PlateHead>
      {clock ? (
        <div className="space-y-1 text-sm">
          <p>
            {capitalize(clock.date_display)} — {pad(clock.hour)}:{pad(clock.minute)}
          </p>
          <p className="text-muted-foreground">
            {capitalize(clock.season)}, {capitalize(clock.phase)}
            {clock.paused && ' · Paused'}
          </p>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">Time is currently frozen.</p>
      )}
      {conditions?.weather_type && (
        <p
          className="mt-1 text-sm text-muted-foreground"
          title={conditions.emit_text ?? undefined}
          data-testid="time-plate-weather"
        >
          {conditions.weather_type} where you stand.
        </p>
      )}
    </Plate>
  );
}

function OccasionsPlate() {
  const { data } = useQuery<PaginatedResponse<EventListItem>>({
    queryKey: ['events', { upcoming: 'true' }],
    queryFn: () => fetchEvents({ upcoming: 'true' }),
  });
  const events = data?.results ?? [];

  return (
    <Plate className="p-4">
      <PlateHead as="h2" className="mb-2">
        Occasions
      </PlateHead>
      {events.length === 0 ? (
        <p className="text-sm text-muted-foreground">No events scheduled. How indolent.</p>
      ) : (
        <div className="divide-y">
          {events.slice(0, 5).map((event) => (
            <EventCard key={event.id} event={event} compact />
          ))}
        </div>
      )}
      <Link to="/events" className="mt-2 inline-block text-sm hover:underline">
        All occasions →
      </Link>
    </Plate>
  );
}

function CrierPlate() {
  const { data } = useGemits({ page: 1 });
  const gemits = data?.results ?? [];

  return (
    <Plate className="p-4">
      <PlateHead as="h2" className="mb-2">
        The Crier
      </PlateHead>
      {gemits.length === 0 ? (
        <p className="text-sm text-muted-foreground">No news. A little unsettling.</p>
      ) : (
        <ul className="space-y-2">
          {gemits.map((gemit) => (
            <li key={gemit.id} className="text-sm">
              <p>{gemit.body}</p>
              <p className="text-xs text-muted-foreground">
                {new Date(gemit.sent_at).toLocaleString()}
              </p>
            </li>
          ))}
        </ul>
      )}
    </Plate>
  );
}

function TidingsDigestPlate({ viewerId }: { viewerId: number }) {
  return (
    <Plate className="p-4">
      <PlateHead as="h2" className="mb-2">
        Your Circles
      </PlateHead>
      <TidingsFeed viewerId={viewerId} />
    </Plate>
  );
}

export function WorldBand() {
  const { entryId: dockedEntryId } = useBrowsingIdentity();

  return (
    <div className="space-y-4">
      <TimePlate />
      <OccasionsPlate />
      <CrierPlate />
      {dockedEntryId != null && <TidingsDigestPlate viewerId={dockedEntryId} />}
    </div>
  );
}
